/**
 * BUILDLIST 69.47 step 1 (DECISIONS 256 (3), 308 — the owner: "For destruction in story I think we still need to introduce
 * that c4 type placed explosive that is much harder to use and much more useful to story objectives"; 69.47's leans, lane H's
 * note sweeps/2026-10-25-69.47-charge.md; design_sheets/specialists.html): the engineer's placed charge, with
 * GameConfig.charges.
 * - **Who:** the engineer (a squad's `engineer` slot, else — the switch's issue at the setup, `issueEngineers` — each squad of
 *   three or more without one names its first plain rifleman left, else its leader). It carries CHARGE.carried (absent = full) and is refilled
 *   to them in reach of a stocked crate (a dump; not a support's case).
 * - **Placing:** a kneel of CHARGE.kneelSeconds (`chargeLeft`), stopped by a shot, a jump, a step, a seat or the air (the
 *   charge kept). Placed, it sits on the face of the nearest structure within CHARGE.reachPeople of the engineer (its shape's
 *   nearest point: set on the plate), else on the ground at its feet.
 * - **Set off:** on a timer (CHARGE.timerSeconds from the placing), or on the wire — by its engineer, alive and within
 *   CHARGE.wirePeople of it (`blowWire` makes them due; the step sets them off). Its burst is the `charge` class (constants/explosions.ts: 150 at the heart,
 *   penetration 9 — every tank face but the front). A burst over a placed charge destroys it; nothing sets it off but its own.
 * - **The toughened targets** (308's "Charge much faster"): with the switch, a story's destroy targets (the air-defence site
 *   and its generator) take CHARGE.tough's share of a blow from the classes a squad carries (CHARGE.toughClasses: the grenade,
 *   the 40 mm, the tubes, the drone's charge, the 20 mm) — many throws to fell — and the charge's whole; artillery's and the
 *   tank's rounds as before.
 * Pure; the Match steps it (`stepCharges`, after the grenades) and takes a person's `charge` input (Match.placeCharge, blowCharges).
 */
import type { GameConfig, GameState, PlayerState, StructureState, UnitIntent, Vec3 } from '../types';
import type { ChargeFuse, PlacedCharge } from '../types/charge';
import type { ExplosionClass } from '../constants/explosions';
import { CHARGE, PHYS_DT } from '../constants';
import { PERSON_BODY_HEIGHT, personSetOf } from './scaleProfile';
import { arcDistance, getPlayerWorldPosition } from './gameState';
import { structureNearest } from './hits';
import { crateStocked, supplyReach } from './supply';
import { isCase } from './supportRole';
import { targetsLeft } from './storyObjectives';
import { zoneTableOf } from './vehicleZones';
import { vecAdd, vecLength, vecNormalize, vecScale, vecSub } from '../math';

/** Is the charge in play: the switch. */
export function chargesOn(config: Pick<GameConfig, 'charges'>): boolean {
  return config.charges === true;
}

/** Is `p` an engineer carrying charges in this match. */
export function isEngineer(state: Pick<GameState, 'config'>, p: Pick<PlayerState, 'role'>): boolean {
  return p.role === 'engineer' && chargesOn(state.config);
}

/** The charges an engineer carries (absent = full); anyone else, none. */
export function chargesLeft(state: Pick<GameState, 'config'>, p: Pick<PlayerState, 'role' | 'demoCharges'>): number {
  return isEngineer(state, p) ? p.demoCharges ?? CHARGE.carried : 0;
}

/** A respawn's charges: full again, no kneel under way (keys only on a unit that had any, so the others' state is as it was). */
export function freshCharges(p: Pick<PlayerState, 'demoCharges' | 'chargeLeft' | 'chargeFuse'>): Pick<PlayerState, 'demoCharges' | 'chargeLeft' | 'chargeFuse'> {
  return p.demoCharges !== undefined || p.chargeLeft !== undefined || p.chargeFuse !== undefined ? { demoCharges: undefined, chargeLeft: undefined, chargeFuse: undefined } : {};
}

/** The world units of `n` people at this scale. */
const people = (state: Pick<GameState, 'config'>, n: number) => n * PERSON_BODY_HEIGHT * personSetOf(state).k;

/**
 * The switch's issue at the setup (after the support's and the crewman's): each squad of three or more without an engineer
 * names its first plain rifleman left — not its leader, not a support or a crewman, nor one with a second weapon; with none
 * left (the Story's squads of three: a leader, a crewman, a support), its leader.
 */
export function issueEngineers(state: GameState): GameState {
  if (!chargesOn(state.config) || !state.squads?.length) return state;
  const named = new Set<number>();
  for (const sq of state.squads) {
    if (sq.unitIds.length < 3) continue;
    const ms = sq.unitIds.map(id => state.players.find(p => p.id === id)).filter((p): p is PlayerState => !!p);
    if (ms.some(p => p.role === 'engineer')) continue;
    const plain = (p: PlayerState) => (p.primary ?? 'rifle') === 'rifle' && !p.secondary && !p.role;
    // (none left — the Story's squads of three are a leader, a crewman and a support — the leader carries them)
    const pick = ms.find(p => plain(p) && p.id !== sq.leader) ?? ms.find(p => plain(p) && p.id === sq.leader);
    if (pick) named.add(pick.id);
  }
  if (!named.size) return state;
  return { ...state, players: state.players.map(p => (named.has(p.id) ? { ...p, role: 'engineer' as const } : p)) };
}

/** The switch's toughening at the setup: every story destroy objective's targets standing (the air-defence site, its generator) are `toughened`. */
export function toughenTargets(state: GameState): GameState {
  if (!chargesOn(state.config)) return state;
  const ids = new Set((state.modeState.objectives ?? []).filter(o => o.kind === 'destroy').flatMap(o => targetsLeft(state, o).map(t => t.structureId)));
  if (!ids.size) return state;
  return { ...state, structures: state.structures.map(s => (ids.has(s.id) && !s.toughened ? { ...s, toughened: true as const } : s)) };
}

/**
 * The share of a burst of class `cls` a structure takes (with the switch: a toughened one, of a class a squad carries, fired
 * by a person, its kind's CHARGE.tough share; else whole). Null when nothing is scaled (the switch off, another class, or no
 * person's: a ship's rounds — the gunship's pods and heavy rockets burst as `law` — are owned by `airstrikeOwner`, not a
 * person, and keep heavy fire's damage, 308).
 */
export function toughShareFor(state: Pick<GameState, 'config'>, cls: ExplosionClass | undefined, byPerson: boolean): ((s: StructureState) => number) | undefined {
  if (!byPerson || !chargesOn(state.config) || !cls || !(CHARGE.toughClasses as readonly ExplosionClass[]).includes(cls)) return undefined;
  return s => (s.toughened ? (CHARGE.tough as Record<string, number>)[s.type] ?? CHARGE.toughDefault : 1);
}

/** Why `p` cannot start placing a charge now, or null: an engineer with one left, alive, on its feet out of any seat, hands free. */
export function chargeRefusal(state: Pick<GameState, 'config'>, p: PlayerState): string | null {
  if (!chargesOn(state.config)) return 'No charges in this match';
  if (!isEngineer(state, p)) return 'Only an engineer carries charges';
  if (chargesLeft(state, p) <= 0) return 'No charges left: refill at a crate';
  if (!p.alive) return 'Dead';
  if (p.airborne || p.mount || p.boarded) return 'On your feet first';
  if (p.chargeLeft !== undefined) return 'Already placing one';
  if (p.plantLeft !== undefined || p.reloadLeft !== undefined || p.switchLeft !== undefined || p.healLeft !== undefined || p.treating !== undefined) return 'Hands busy';
  return null;
}

/** Start `unitId`'s kneel to place a charge on `fuse` (the caller checked `chargeRefusal`). */
export function startCharge(state: GameState, unitId: number, fuse: ChargeFuse): GameState {
  return { ...state, players: state.players.map(p => (p.id === unitId ? { ...p, chargeLeft: CHARGE.kneelSeconds, chargeFuse: fuse } : p)) };
}

/**
 * Where `p`'s charge goes: on the face of the nearest standing structure whose shape lies within CHARGE.reachPeople (its
 * nearest point, a hair back toward the engineer), else on the ground at its feet.
 */
export function chargeSpot(state: GameState, p: PlayerState): { point: Vec3; structureId?: number } {
  const feet = getPlayerWorldPosition(p, state.planets);
  const reach = people(state, CHARGE.reachPeople), R = state.planets[p.planetIndex].radius;
  let best: { dist: number; at: Vec3; id: number } | null = null;
  for (const s of state.structures) {
    if (s.health <= 0 || s.planetIndex !== p.planetIndex || arcDistance(s.surfaceDir, p.surfaceDir, R) > reach + people(state, 30)) continue;   // (a coarse cut first: nothing 30 people past the reach)
    const near = structureNearest(feet, s, state);
    if (near && near.dist <= reach && (!best || near.dist < best.dist || (near.dist === best.dist && s.id < best.id))) best = { dist: near.dist, at: near.at, id: s.id };
  }
  if (!best) return { point: feet };
  const back = vecSub(feet, best.at), len = vecLength(back), hair = people(state, 0.03);
  return { point: len > hair ? vecAdd(best.at, vecScale(back, hair / len)) : best.at, structureId: best.id };
}

/**
 * 69.47 step 2 (308's lean: "charges on hulls and AD sites only, grenades on the light and soft ones"): what a bot engineer
 * sets a charge against — with the switch, a standing story destroy target (toughened), an air-defence site, or a hull.
 */
export function chargeWorthy(state: Pick<GameState, 'config'>, s: StructureState): boolean {
  return chargesOn(state.config) && s.health > 0 && (!!s.toughened || s.type === 'pointDefence' || zoneTableOf(s) !== null);
}

/** 69.47 step 2: a charge of side `team` placed against structure `id`, not yet gone off (its burst is on the way: hold the HE). */
export function teamChargeOn(state: Pick<GameState, 'charges'>, team: number, id: number): PlacedCharge | null {
  return (state.charges ?? []).find(c => c.team === team && c.structureId === id) ?? null;
}

/** 69.47 step 2: the charge engineer `p` set on the timer that is still to go off within CHARGE.backOffPeople of it, or null. */
export function ownChargeNear(state: Pick<GameState, 'charges' | 'planets' | 'config'>, p: PlayerState): PlacedCharge | null {
  const at = getPlayerWorldPosition(p, state.planets), off = people(state, CHARGE.backOffPeople);
  return (state.charges ?? []).find(c => c.ownerId === p.id && c.fuse === 'timer' && c.planetIndex === p.planetIndex && vecLength(vecSub(c.point, at)) < off) ?? null;
}

/**
 * 69.47 step 2: where on structure `s`'s face a bot engineer stands to set a charge — the face's nearest point to `from` (its
 * feet), as a direction on the ground — and how far its feet are from that face now.
 */
export function chargeStand(state: GameState, s: StructureState, from: Vec3): { dir: Vec3; dist: number } | null {
  const near = structureNearest(from, s, state), planet = state.planets[s.planetIndex];
  return near && planet ? { dir: vecNormalize(vecSub(near.at, planet.position)), dist: near.dist } : null;
}

export type ChargeEvent =
  | { kind: 'chargeSet'; unitId: number; chargeId: number; fuse: ChargeFuse; structureId?: number }
  | { kind: 'chargeStopped'; unitId: number }
  | { kind: 'chargesRefilled'; unitId: number; charges: number }
  | { kind: 'chargeLost'; chargeId: number };

/** On its feet and out of any seat. */
const grounded = (p: PlayerState) => p.alive && !p.airborne && !p.mount && !p.boarded;

/**
 * One tick (after the grenades): the kneels run (placed when they run out; stopped by a shot, a jump, a step, a seat or the
 * air), the timers due go off (returned as `bursts`, for the Match's blast), the charges a burst this tick lay over are lost
 * (`blasts`: where this tick's bursts went off, each with its kill radius in world units), and an engineer short of its
 * charges in reach of a stocked crate is refilled. `intents` is indexed like `players`. Null when nothing changed.
 */
export function stepCharges(state: GameState, intents: readonly (UnitIntent | undefined)[], tick: number, dt: number, blasts: ReadonlyArray<{ planetIndex: number; point: Vec3; radius: number }> = []): { state: GameState; bursts: PlacedCharge[]; events: ChargeEvent[] } | null {
  if (!chargesOn(state.config)) return null;
  let players: PlayerState[] | null = null;
  let charges: PlacedCharge[] | null = null;
  const events: ChargeEvent[] = [];
  let nextId = state.chargesSet ?? 0;
  const reach = supplyReach(state);
  for (let i = 0; i < state.players.length; i++) {
    const p = state.players[i], intent = intents[i];
    if (p.chargeLeft !== undefined) {
      if (!grounded(p) || intent?.fire || intent?.jump || (intent && (intent.forward !== 0 || intent.right !== 0))) {
        (players ??= state.players.slice())[i] = { ...p, chargeLeft: undefined, chargeFuse: undefined };
        events.push({ kind: 'chargeStopped', unitId: p.id });
        continue;
      }
      if (p.chargeLeft - dt > 0) { (players ??= state.players.slice())[i] = { ...p, chargeLeft: p.chargeLeft - dt }; continue; }
      const spot = chargeSpot(state, p), fuse = p.chargeFuse ?? 'timer', id = nextId++;
      const placed: PlacedCharge = { id, team: p.teamIndex, ownerId: p.id, planetIndex: p.planetIndex, point: spot.point, surfaceDir: vecNormalize(p.surfaceDir), fuse, placedTick: tick,
        ...(fuse === 'timer' ? { blowTick: tick + Math.max(1, Math.round(CHARGE.timerSeconds / PHYS_DT)) } : {}), ...(spot.structureId !== undefined ? { structureId: spot.structureId } : {}) };
      (charges ??= (state.charges ?? []).slice()).push(placed);
      (players ??= state.players.slice())[i] = { ...p, chargeLeft: undefined, chargeFuse: undefined, demoCharges: chargesLeft(state, p) - 1 };
      events.push({ kind: 'chargeSet', unitId: p.id, chargeId: id, fuse, ...(spot.structureId !== undefined ? { structureId: spot.structureId } : {}) });
      continue;
    }
    // refilled at a dump: an engineer short of its charges, on its feet within a stocked crate's reach (not a support's case)
    if (p.demoCharges !== undefined && p.demoCharges < CHARGE.carried && grounded(p) && isEngineer(state, p)) {
      const R = state.planets[p.planetIndex].radius;
      if (state.structures.some(s => !isCase(s) && crateStocked(s) && s.planetIndex === p.planetIndex && arcDistance(s.surfaceDir, p.surfaceDir, R) <= reach)) {
        (players ??= state.players.slice())[i] = { ...p, demoCharges: undefined };
        events.push({ kind: 'chargesRefilled', unitId: p.id, charges: CHARGE.carried });
      }
    }
  }
  // the timers due, and the charges a burst lay over (lost, not set off)
  const list = charges ?? state.charges ?? [];
  const bursts = list.filter(c => c.blowTick !== undefined && c.blowTick <= tick);
  const lost = list.filter(c => !bursts.includes(c) && blasts.some(b => b.planetIndex === c.planetIndex && vecLength(vecSub(b.point, c.point)) <= b.radius));
  for (const c of lost) events.push({ kind: 'chargeLost', chargeId: c.id });
  if (bursts.length || lost.length) charges = list.filter(c => !bursts.includes(c) && !lost.includes(c));
  if (!players && !charges && nextId === (state.chargesSet ?? 0)) return null;
  return { state: { ...state, ...(players ? { players } : {}), ...(charges ? { charges } : {}), ...(nextId !== (state.chargesSet ?? 0) ? { chargesSet: nextId } : {}) }, bursts, events };
}

/** `unitId`'s wire: its charges on the wire within CHARGE.wirePeople of it, due now (`blowTick`: the step sets them off this tick); none in reach: null. */
export function blowWire(state: GameState, unitId: number, tick: number): { state: GameState; charges: number } | null {
  const p = state.players.find(x => x.id === unitId);
  if (!p || !p.alive || !state.charges?.length) return null;
  const R = state.planets[p.planetIndex]?.radius ?? 1, reach = people(state, CHARGE.wirePeople);
  const due = (c: PlacedCharge) => c.ownerId === unitId && c.fuse === 'wire' && c.blowTick === undefined && c.planetIndex === p.planetIndex && arcDistance(c.surfaceDir, p.surfaceDir, R) <= reach;
  const n = state.charges.filter(due).length;
  if (!n) return null;
  return { state: { ...state, charges: state.charges.map(c => (due(c) ? { ...c, blowTick: tick } : c)) }, charges: n };
}
